import { findVariant, mergeTheme, nocturne, type Theme } from '@ce/theme';
import type { ResolvedBundle, TenantBundle } from './types';
import { nocturneBundle } from './sample/nocturne';
import { defaultLevels } from './venue/layout';

/**
 * The CMS client.
 *
 * Contract: `GET {baseUrl}/tenants/{tenantId}/bundle` returns a TenantBundle.
 * Strategy is cache-first with background revalidation — the app must open
 * instantly on a convention-centre wifi network that may or may not exist, so a
 * cached bundle always wins the first paint and the network result is applied
 * when it lands.
 */

export interface Storage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}

/** In-memory fallback so the client works with no storage wired up. */
export class MemoryStorage implements Storage {
  private map = new Map<string, string>();
  async getItem(key: string) { return this.map.get(key) ?? null; }
  async setItem(key: string, value: string) { this.map.set(key, value); }
}

export interface CmsConfig {
  baseUrl: string;
  tenantId: string;
  storage?: Storage;
  /** Bundled payload used when the network fails and nothing is cached. */
  fallback?: TenantBundle;
  fetchImpl?: typeof fetch;
}

const CACHE_PREFIX = 'ce.bundle.';

/**
 * Resolves the theme in three layers: Nocturne defaults, then the tenant's
 * brand from the CMS, then the attendee's chosen variant. The attendee can only
 * ever pick from what the client approved, so brand control survives.
 */
export function resolveBundle(bundle: TenantBundle, themeId: string | null = null): ResolvedBundle {
  const base: Theme = mergeTheme(nocturne, bundle.theme);
  const variant = findVariant(bundle.themeVariants, themeId);
  const theme: Theme = variant ? mergeTheme(base, variant.patch) : base;
  // Venue levels are optional in the payload; fall back to the generated plan.
  const venue = bundle.venue.levels.length
    ? bundle.venue
    : { ...bundle.venue, levels: defaultLevels() };
  return { bundle: { ...bundle, venue }, theme };
}

export class CmsClient {
  private cfg: CmsConfig;
  private storage: Storage;
  private doFetch: typeof fetch;

  constructor(cfg: CmsConfig) {
    this.cfg = cfg;
    this.storage = cfg.storage ?? new MemoryStorage();
    this.doFetch = cfg.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  }

  private get cacheKey() {
    return `${CACHE_PREFIX}${this.cfg.tenantId}`;
  }

  private async readCache(): Promise<TenantBundle | null> {
    try {
      const raw = await this.storage.getItem(this.cacheKey);
      return raw ? (JSON.parse(raw) as TenantBundle) : null;
    } catch {
      return null;
    }
  }

  private async writeCache(bundle: TenantBundle) {
    try {
      await this.storage.setItem(this.cacheKey, JSON.stringify(bundle));
    } catch {
      // A full disk is not a reason to fail a launch.
    }
  }

  /** Network fetch. Throws on any non-2xx or transport error. */
  async fetchBundle(): Promise<TenantBundle> {
    const url = `${this.cfg.baseUrl.replace(/\/$/, '')}/tenants/${this.cfg.tenantId}/bundle`;
    const res = await this.doFetch(url, { headers: { accept: 'application/json' } });
    if (!res.ok) throw new Error(`CMS ${res.status} for ${url}`);
    return (await res.json()) as TenantBundle;
  }

  /**
   * Cache-first load. Returns immediately with whatever is available, then
   * calls `onUpdate` if the network returns a newer revision.
   */
  async load(onUpdate?: (b: ResolvedBundle) => void): Promise<ResolvedBundle> {
    const cached = await this.readCache();
    const fallback = this.cfg.fallback ?? nocturneBundle;
    const first = resolveBundle(cached ?? fallback);

    const revalidate = async () => {
      try {
        const fresh = await this.fetchBundle();
        if (fresh.revision !== (cached ?? fallback).revision) {
          await this.writeCache(fresh);
          onUpdate?.(resolveBundle(fresh));
        }
      } catch {
        // Offline is a normal state at a convention. Keep the cached bundle.
      }
    };

    if (cached) {
      void revalidate();
      return first;
    }

    // Nothing cached: try the network once before falling back, so a first
    // launch on a good connection gets real content rather than the sample.
    try {
      const fresh = await this.fetchBundle();
      await this.writeCache(fresh);
      return resolveBundle(fresh);
    } catch {
      return first;
    }
  }
}
